import React, { useState, useEffect } from "react";
import { useAsignaturas } from "../../context/AsignaturasContext";
import { useUsers } from "../../context/UsersContext";
import { FaPlus, FaTrash, FaCheck } from "react-icons/fa";

const AsignacionMasiva = ({ asignaturasConSalones, showNotification }) => {
  const { asignarMasivo } = useAsignaturas();
  const { salones, fetchSalones } = useUsers();

  const [salonesSeleccionados, setSalonesSeleccionados] = useState([]);
  const [asignacionesConfig, setAsignacionesConfig] = useState([]);

  const asignaturasOrdenadas = asignaturasConSalones;

  useEffect(() => {
    fetchSalones();
  }, [fetchSalones]);

  const toggleSalon = (salonId) => {
    setSalonesSeleccionados((prev) =>
      prev.includes(salonId)
        ? prev.filter((id) => id !== salonId)
        : [...prev, salonId]
    );
  };

  const agregarAsignatura = () => {
    setAsignacionesConfig([
      ...asignacionesConfig,
      { asignatura_id: "", IH: "" },
    ]);
  };

  const eliminarAsignatura = (index) => {
    setAsignacionesConfig(asignacionesConfig.filter((_, i) => i !== index));
  };

  const actualizarAsignacion = (index, field, value) => {
    const nuevasAsignaciones = [...asignacionesConfig];
    nuevasAsignaciones[index][field] = value;
    setAsignacionesConfig(nuevasAsignaciones);
  };

  const handleAsignarMasivamente = async () => {
    if (salonesSeleccionados.length === 0) {
      showNotification({
        type: "error",
        message: "Debe seleccionar al menos un salón",
      });
      return;
    }

    if (asignacionesConfig.length === 0) {
      showNotification({
        type: "error",
        message: "Debe agregar al menos una asignatura",
      });
      return;
    }

    // Validar que todas las asignaturas tengan datos completos
    const invalidas = asignacionesConfig.some((a) => !a.asignatura_id);

    if (invalidas) {
        showNotification({
        type: "error",
        message: "Todas las asignaturas deben estar seleccionadas",
        });
        return;
    }

    // Crear todas las combinaciones salon-asignatura
    const asignaciones = [];
    for (const salonId of salonesSeleccionados) {
      for (const config of asignacionesConfig) {
        asignaciones.push({
          salon_id: salonId,
          asignatura_id: config.asignatura_id,
          IH: config.IH || null,
        });
      }
    }

    try {
      const resultado = await asignarMasivo(asignaciones);

      showNotification({
        type: resultado.data.fallidas > 0 ? "warning" : "success",
        message: resultado.message,
      });

      // Limpiar formulario
      setSalonesSeleccionados([]);
      setAsignacionesConfig([]);
    } catch (error) {
      showNotification({
        type: "error",
        message: "Error al realizar asignación masiva",
      });
    }
  };

  return (
    <div className="bg-white p-6 rounded-2xl shadow-md">
      <h3 className="text-xl font-semibold mb-4">Asignación Masiva</h3>

      {/* Selección de salones */}
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3">1. Seleccionar Salones</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-4">
          {salones?.map((salon) => (
            <label
              key={salon.id}
              className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
            >
              <input
                type="checkbox"
                checked={salonesSeleccionados.includes(salon.id)}
                onChange={() => toggleSalon(salon.id)}
                className="w-4 h-4 text-blue-600"
              />
              <span className="text-sm">
                {salon.grado} - {salon.nombre}
              </span>
            </label>
          ))}
        </div>
        <p className="text-sm text-gray-500 mt-2">
          {salonesSeleccionados.length} salon(es) seleccionado(s)
        </p>
      </div>

      {/* Configuración de asignaturas */}
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-3">
          2. Configurar Asignaturas
        </h4>

        <div className="space-y-3">
          {asignacionesConfig.map((config, index) => (
            <div
              key={index}
              className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg"
            >
              <div className="flex-1">
                <select
                  value={config.asignatura_id}
                  onChange={(e) =>
                    actualizarAsignacion(index, "asignatura_id", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Seleccionar asignatura</option>
                  {asignaturasConSalones?.map((asig) => (
                    <option key={asig.asignatura_id} value={asig.asignatura_id}>
                      {asig.nombre}
                      {asig.descripcion &&
                        asig.descripcion.trim() !== "" &&
                        ` - (Dimensión ${asig.descripcion})`}
                    </option>
                  ))}
                </select>
              </div>

              <div className="w-32">
                <input
                  type="number"
                  min="1"
                  value={config.IH}
                  onChange={(e) =>
                    actualizarAsignacion(index, "IH", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="I.H (Opcional)"
                />
              </div>

              <button
                onClick={() => eliminarAsignatura(index)}
                className="text-red-600 hover:text-red-800 transition-colors"
                title="Eliminar"
              >
                <FaTrash size={16} />
              </button>
            </div>
          ))}
        </div>

        <button
          onClick={agregarAsignatura}
          className="mt-3 flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
        >
          <FaPlus /> Agregar Asignatura
        </button>
      </div>

      {/* Resumen y botón de asignación */}
      <div className="border-t pt-4">
        <div className="bg-blue-50 p-4 rounded-lg mb-4">
          <h5 className="font-semibold text-blue-900 mb-2">Resumen:</h5>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• {salonesSeleccionados.length} salones seleccionados</li>
            <li>• {asignacionesConfig.length} asignaturas configuradas</li>
            <li>
              • Total de asignaciones:{" "}
              <strong>
                {salonesSeleccionados.length * asignacionesConfig.length}
              </strong>
            </li>
          </ul>
        </div>

        <button
          onClick={handleAsignarMasivamente}
          disabled={
            salonesSeleccionados.length === 0 ||
            asignacionesConfig.length === 0
          }
          className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-green text-white rounded-lg font-semibold hover:bg-green-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          <FaCheck /> Asignar Masivamente
        </button>
      </div>
    </div>
  );
};

export default AsignacionMasiva;